import { useEffect, useState } from "react";
import { Coins, Check, Loader2 } from "lucide-react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta, eur } from "@/lib/format";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { BillingDialog } from "@/components/app/BillingDialog";

const ORDER = ["credits_10", "credits_50", "credits_200", "credits_500"];
const RETURN_URL = "/app/credits";

export default function Credits() {
  const t = useT();
  usePageMeta(t("credits.meta_title"));
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState(null);
  const [buying, setBuying] = useState(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    api.get("/credits/packages").then((r) => setPackages(r.data || {})).catch(() => setPackages({}));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("checkout");
    if (status === "success") {
      toast.success(t("credits.payment_received"));
      refreshUser().catch(() => {});
      window.history.replaceState(null, "", RETURN_URL);
    } else if (status === "cancel") {
      toast(t("credits.purchase_cancelled"));
      window.history.replaceState(null, "", RETURN_URL);
    }
  }, [refreshUser, t]);

  const buy = async (pkg) => {
    setBuying(pkg);
    try {
      const { data } = await api.post("/credits/checkout", {
        package: pkg,
        origin_url: window.location.origin + RETURN_URL,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(t("credits.granted", { n: data.granted_credits || packages[pkg]?.credits }));
      await refreshUser();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.error === "billing_required") {
        setPending(pkg);
        setBillingOpen(true);
      } else {
        toast.error(errMessage(err, t("credits.failed")));
      }
    } finally {
      setBuying(null);
    }
  };

  const onBillingSaved = () => {
    setBillingOpen(false);
    if (pending) {
      const p = pending;
      setPending(null);
      buy(p);
    }
  };

  const packs = packages ? ORDER.filter((k) => packages[k]).map((k) => ({ key: k, ...packages[k] })) : null;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-foreground">{t("credits.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("credits.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
          <Coins className="h-4 w-4 text-sage-light" />
          <span className="text-sm font-medium">{user?.credits ?? 0} {t("credits.suffix")}</span>
        </div>
      </div>

      {packs === null ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {packs.map((p) => {
            const best = p.key === "credits_200";
            return (
              <div key={p.key} className={`relative flex flex-col rounded-2xl border bg-card p-6 ${best ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                {best && <Badge className="absolute -top-2.5 left-6 rounded-full">{t("credits.popular")}</Badge>}
                <p className="font-display text-4xl font-semibold text-foreground">{p.credits}</p>
                <p className="text-sm text-muted-foreground">{t("credits.suffix")}</p>
                <p className="mt-4 font-display text-2xl text-primary">{eur(p.amount)}</p>
                <p className="text-xs text-muted-foreground">{t("credits.incl_vat")} · {eur(Math.round(p.amount / p.credits))}{t("credits.per_credit")}</p>
                <Button
                  className="mt-5 w-full rounded-full"
                  onClick={() => buy(p.key)}
                  disabled={!!buying}
                  data-testid={`buy-${p.key}`}
                >
                  {buying === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : t("credits.buy")}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-start gap-3 rounded-2xl border border-border bg-secondary/40 p-5 text-sm text-muted-foreground">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
        <p>{t("credits.secure")}</p>
      </div>

      <BillingDialog open={billingOpen} onOpenChange={setBillingOpen} onSaved={onBillingSaved} />
    </div>
  );
}
